/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useEffect, useState } from "react";
import { Button } from "@vector-im/compound-web";

import styles from "./MeetSettings.module.css";

function describe(perm: NotificationPermission): string {
  if (perm === "granted") return "Enabled — you'll be notified of incoming calls in this browser.";
  if (perm === "denied") return "Blocked by the browser. Enable it in your browser's site settings.";
  return "Not requested yet.";
}

export const NotificationsSettingsTab: FC = () => {
  const [perm, setPerm] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "denied",
  );

  useEffect(() => {
    if (typeof Notification === "undefined") return;
    setPerm(Notification.permission);
  }, []);

  const request = async (): Promise<void> => {
    if (typeof Notification === "undefined") return;
    const result = await Notification.requestPermission();
    setPerm(result);
  };

  return (
    <div>
      <h3 className={styles.sectionTitle}>Browser Notifications</h3>
      <p className={styles.small}>
        Calls are powered by your Nevai homeserver; incoming-call alerts are
        delivered by your browser's notification system. Enable them here.
        Everything stays local — no external server is involved.
      </p>
      <p className={styles.small}>
        <strong>Status:</strong> {describe(perm)}
      </p>
      <Button
        disabled={perm === "granted" || perm === "denied"}
        onClick={() => void request()}
      >
        {perm === "granted" ? "Notifications enabled" : "Enable notifications"}
      </Button>
    </div>
  );
};
